import { useNavigate } from 'react-router-dom'
import { LogOut, Bug, RefreshCw } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import * as Sentry from '@sentry/react'
import logoLaranja from '../../assets/logo-laranja.png'
import { useState } from 'react'
import Modal from './Modal'
import { resetSheetCaches } from '../../services/api'

export default function Header() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)
  const [isResetLoading, setIsResetLoading] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSuccess, setResetSuccess] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const handleTestSentry = () => {
    // Testar captura de erro no Sentry
    try {
      throw new Error('Erro de teste do Sentry - Frontend doBeco')
    } catch (error) {
      Sentry.captureException(error)
      console.log('Erro de teste enviado para o Sentry')
    }
  }

  const handleResetCache = async () => {
    setResetError(null)
    setIsResetLoading(true)
    
    try {
      await resetSheetCaches()
      setResetSuccess(true)
      setTimeout(() => {
        setIsResetModalOpen(false)
        setResetSuccess(false)
        window.location.reload()
      }, 2000)
    } catch (error) {
      setResetError(error instanceof Error ? error.message : 'Falha ao resetar caches de planilha')
      setIsResetLoading(false)
    }
  }

  const openResetModal = () => {
    setIsResetModalOpen(true)
    setResetError(null)
    setResetSuccess(false)
  }

  const closeResetModal = () => {
    setIsResetModalOpen(false)
    setResetError(null)
    setResetSuccess(false)
    setIsResetLoading(false)
  }

  return (
    <header className="bg-white shadow-sm border-b border-brand-neutral-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <button
            onClick={() => navigate('/manutencao')}
            className="flex items-center hover:opacity-80 transition-opacity cursor-pointer"
            title="Voltar para lista de requisições"
            aria-label="Voltar para lista de requisições"
          >
            <img
              src={logoLaranja}
              alt="doBeco Padaria"
              className="h-10 w-auto"
            />
          </button>

          {/* Botão de teste do Sentry (apenas em desenvolvimento) */}
          {import.meta.env.DEV && (
            <button
              onClick={handleTestSentry}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 focus:ring-offset-brand-surface-50 transition-colors"
              title="Testar envio de erro para o Sentry"
            >
              <Bug className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Testar Sentry</span>
            </button>
          )}

          {/* Informações do usuário e logout */}
          <div className="flex items-center space-x-4">
            {user && (
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-brand-text-primary">{user.name}</p>
                <p className="text-xs text-brand-text-muted capitalize">{user.type}</p>
              </div>
            )}
            
            {/* Botão de reset de cache (apenas admins) */}
            {user && user.type === 'admin' && (
              <button
                onClick={openResetModal}
                className="inline-flex items-center p-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:ring-offset-brand-surface-50 transition-colors group"
                title="Recarregar dados das planilhas"
                aria-label="Recarregar dados das planilhas"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            )}
            
            <button
              onClick={handleLogout}
              className="inline-flex items-center p-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 focus:ring-offset-brand-surface-50 transition-colors group"
              title="Sair"
              aria-label="Sair"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal de confirmação de reset de cache */}
      <Modal
        isOpen={isResetModalOpen}
        onClose={closeResetModal}
        title="Recarregar dados das planilhas"
        size="sm"
        // Esconde o botão "Fechar" do rodapé, mantendo apenas o X no cabeçalho
        showCloseButton={false}
      >
        <div className="text-center">
          {resetSuccess ? (
            <div>
              <div className="mb-4 text-green-600">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Dados recarregados com sucesso!
              </h3>
              <p className="text-sm text-gray-600">
                A página será recarregada automaticamente...
              </p>
            </div>
          ) : (
            <div>
              <div className="mb-4 text-orange-500">
                <RefreshCw className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Confirmar recarregamento de dados
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Esta ação vai limpar todos os caches de dados vindos das planilhas.
                As próximas consultas vão ler diretamente do Google Sheets.
              </p>
              
              {resetError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{resetError}</p>
                </div>
              )}

              <div className="flex space-x-3 justify-center">
                <button
                  onClick={closeResetModal}
                  disabled={isResetLoading}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleResetCache}
                  disabled={isResetLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary-500 hover:bg-brand-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResetLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12c0 5.523 4.477 10 10 10s10-4.477 10-10h-4c0 4.418-3.582 8-8 8z"></path>
                      </svg>
                      Processando...
                    </div>
                  ) : (
                    'Confirmar'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </header>
  )
}
